import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import portalApi, { getPortalToken, setPortalToken } from "../../lib/portalApi";
import { LayoutGrid, LogOut, ChevronRight, Loader2, FolderKanban, CalendarClock } from "lucide-react";

const STATUS_LABEL = { nouveau: "Nouveau", etude: "En étude", devis: "Devis", production: "En production", controle: "Contrôle qualité", termine: "Terminé", livree: "Livré", facturee: "Facturé" };

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [company, setCompany] = useState({});
  const [me, setMe] = useState({});

  useEffect(() => {
    if (!getPortalToken()) { navigate("/portail", { replace: true }); return; }
    portalApi.get("/portal/me").then((r) => { setCompany(r.data.company); setMe(r.data.client); }).catch(() => { setPortalToken(null); navigate("/portail", { replace: true }); });
    portalApi.get("/portal/projects").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, [navigate]);

  const logout = () => { setPortalToken(null); navigate("/portail", { replace: true }); };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-[#131b33] to-[#0b1224] text-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><LayoutGrid className="w-6 h-6 text-cognac-400" strokeWidth={2.4} /><span className="font-display font-bold text-lg">{company.name || "FABRIKO"}</span></div>
          <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white"><LogOut className="w-4 h-4" /> Quitter</button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-display font-bold text-navy text-2xl">Bonjour {me.name || ""} 👋</h1>
        <p className="text-slate-500 mt-1 mb-6">Suivez l'avancement de vos projets confiés à l'atelier.</p>

        {projects === null ? (
          <div className="flex items-center gap-2 text-slate-400 py-12 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Chargement…</div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center text-slate-400" data-testid="portal-empty"><FolderKanban className="w-10 h-10 mx-auto mb-3 opacity-40" />Aucun projet à suivre pour le moment.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="portal-projects">
            {projects.map((p) => (
              <button key={p.id} onClick={() => navigate(`/portail/projets/${p.id}`)} data-testid={`portal-project-${p.id}`} className="text-left bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md hover:border-cognac-300 transition-all group">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><span className="text-[11px] font-mono text-slate-400">{p.reference}</span><h3 className="font-display font-bold text-navy truncate">{p.title}</h3></div>
                  <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-cognac-500 shrink-0" />
                </div>
                <div className="mt-1 text-xs text-slate-500">{p.support_label || p.nature || "Projet sellerie"}</div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1"><span className="font-semibold text-cognac-600">{STATUS_LABEL[p.status] || p.status}</span><span className="text-slate-400">{p.progress}%</span></div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-gradient-to-r from-cognac-400 to-cognac-500 rounded-full transition-all" style={{ width: `${p.progress}%` }} /></div>
                </div>
                {p.due_date && <div className="mt-3 text-[11px] text-slate-400 inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Échéance {p.due_date}</div>}
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
